import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { pulseProjectApi } from 'container/pulseProjectApi';
import { projectApi } from 'container/projectApi';
import { PulseTemplateModel } from 'api/dto/PulseTemplate.dto';
import { mapProjectDtoToModel, ProjectModel } from 'api/dto/Project.dto';
import { ProjectKind } from 'model/ProjectKind';
import { ProjectStatus } from 'model/ProjectStatus';
import { LifecycleType } from 'model/Lifecycle';
import { redirectToClientDashboard } from 'reduxStore/auth/utils';
import { AdminState } from 'reduxStore/appStore';
import {
  selectCurrentProjectId,
  selectCurrentProjectResource,
  selectSubProjectCurrentId,
  selectSubProjectsPageQuery,
} from 'reduxStore/project/projectSelector';
import { openSnackbar } from 'reduxStore/snackbars/asyncActions';
import { serializeError } from 'shared/utils/redux';
import { PulseProjectApi } from 'api/PulseProjectApi';

import { STORE_NAME } from '../initialState';

import { selectSubProjectsDomain } from './selectors';

export const fetchCurrentPulseProject = createAsyncThunk(
  `${STORE_NAME}fetchCurrentPulseProject`,
  async () => {
    const result = await pulseProjectApi.fetch();
    redirectToClientDashboard({ id: result.project.id });
    return result;
  },
  { serializeError },
);

export const getPulseProject = createAsyncThunk(
  `${STORE_NAME}getPulseProject`,
  (_, { getState }) => {
    const pulseTemplateProjectId = selectCurrentProjectId(getState());

    return pulseProjectApi.get(pulseTemplateProjectId);
  },
  { serializeError },
);

export const getPulseProjectFast = createAsyncThunk(
  `${STORE_NAME}/getPulseProjectFast`,
  async (_, { getState }): Promise<PulseTemplateModel> => {
    const pulseTemplateProjectId = selectCurrentProjectId(getState());
    const project = await projectApi.get(pulseTemplateProjectId);

    return {
      hasChildProjectsAnyPublishableSubmits: false,
      project,
      pulseProjects: [],
      pulseOnDemandPolls: [],
      pulseLifecycleProjects: [],
    };
  },
  { serializeError },
);

export const refreshPulseProjectData = createAsyncThunk(
  `${STORE_NAME}/refreshPulseProjectData`,
  (_, { getState }) => {
    const pulseTemplateProjectId = selectCurrentProjectId(getState());

    return projectApi.get(pulseTemplateProjectId);
  },
  { serializeError },
);

export const fetchSubProjectsList = createAsyncThunk(
  `${STORE_NAME}/fetchSubProjectsList`,
  ({ config }: { config?: Partial<PulseProjectApi.FetchSubProjectsQuery> }, { getState }) => {
    const pageQuery: PulseProjectApi.FetchSubProjectsQuery = selectSubProjectsPageQuery(getState());
    const pulseTemplateProjectId = selectCurrentProjectId(getState());

    return pulseProjectApi.getSubProjects(pulseTemplateProjectId, {
      ...pageQuery,
      ...config,
    });
  },
  { serializeError },
);

export const fetchLiveAndScheduledSurveys = createAsyncThunk(
  `${STORE_NAME}/fetchLiveAndScheduledSurveys`,
  async (_, { getState }): Promise<ProjectModel[]> => {
    const pulseTemplateProjectId = selectCurrentProjectId(getState());
    const projects = await pulseProjectApi.getChildProjects(pulseTemplateProjectId);

    return projects
      .filter(
        (project) =>
          project.kind === ProjectKind.PULSE_PROJECT &&
          (project.status === ProjectStatus.Live || project.status === ProjectStatus.InDevelopment),
      )
      .map(mapProjectDtoToModel);
  },
  { serializeError },
);

export const createPulseProject = createAsyncThunk(
  `${STORE_NAME}/createPulseProject`,
  (
    {
      pulseFrequencyData,
    }: {
      pulseFrequencyData: PulseProjectApi.PulseFrequencyData;
    },
    { getState },
  ) => {
    const pulseTemplateProjectId = selectCurrentProjectId(getState());

    return pulseProjectApi.create(pulseTemplateProjectId, pulseFrequencyData);
  },
  { serializeError },
);

export const changePulseFrequency = createAsyncThunk(
  `${STORE_NAME}/changePulseFrequency`,
  (
    {
      pulseFrequencyData,
    }: {
      pulseFrequencyData: PulseProjectApi.ChangeFrequencyData;
    },
    { getState },
  ) => {
    const pulseTemplateProjectId = selectCurrentProjectId(getState());

    return pulseProjectApi.updateFrequency(pulseTemplateProjectId, pulseFrequencyData);
  },
  { serializeError },
);

export const distributeLifecycle = createAsyncThunk(
  `${STORE_NAME}/distributeLifecycle`,
  (
    {
      lifecycleType,
      employeesIds,
    }: {
      lifecycleType: LifecycleType;
      employeesIds: number[];
    },
    { getState },
  ) => {
    const pulseTemplateProjectId = selectCurrentProjectId(getState());
    return pulseProjectApi.distributeLifecycle(pulseTemplateProjectId, lifecycleType, employeesIds);
  },
  { serializeError },
);

export const uploadLifecycleAudience = createAsyncThunk(
  `${STORE_NAME}/uploadLifecycleAudience`,
  ({ lifecycleType, file }: { lifecycleType: LifecycleType; file: File }, { getState }) => {
    const pulseTemplateProjectId = selectCurrentProjectId(getState());
    return pulseProjectApi.uploadRespondents(pulseTemplateProjectId, lifecycleType, file);
  },
  { serializeError },
);

export const fetchAiContext = createAsyncThunk(
  `${STORE_NAME}/fetchAiContext`,
  (_, { getState }) => {
    const pulseProjectId = selectSubProjectCurrentId(getState());
    return pulseProjectApi.getAiContext(pulseProjectId);
  },
  { serializeError },
);

export const updateAiContext = createAsyncThunk(
  `${STORE_NAME}/updateAiContext`,
  ({ pulseAIContext }: { pulseAIContext: string }, { getState }) => {
    const pulseProjectId = selectSubProjectCurrentId(getState());
    return pulseProjectApi.updateAiContext(pulseProjectId, pulseAIContext);
  },
  { serializeError },
);

export const renamePulseProject = createAction<{ projectId: number; name: string }>(
  `${STORE_NAME}/renamePulseProject`,
);

const selectPulseProjectName = (state: AdminState, projectId: number): string | undefined => {
  const currentProject = selectCurrentProjectResource(state)?.data;
  const subProjects = selectSubProjectsDomain(state);
  const templateProjects = PulseTemplateModel.isPulseTemplateModel(currentProject)
    ? [...currentProject.pulseProjects, ...currentProject.pulseOnDemandPolls]
    : [];
  return [
    ...templateProjects,
    ...(subProjects?.list?.data ?? []),
    ...(subProjects?.liveAndScheduled?.data ?? []),
  ].find((project) => project.id === projectId)?.name;
};

export const renamePulseSurvey = createAsyncThunk(
  `${STORE_NAME}/renamePulseSurvey`,
  async (
    { projectId, name }: { projectId: number; name: string },
    { dispatch, getState, rejectWithValue },
  ) => {
    const previousName = selectPulseProjectName(getState(), projectId);
    dispatch(renamePulseProject({ projectId, name }));

    try {
      await projectApi.rename(projectId, name);
    } catch (error) {
      if (previousName !== undefined) {
        dispatch(renamePulseProject({ projectId, name: previousName }));
      }
      dispatch(openSnackbar({ message: 'Could not rename survey. Please try again.' }));
      return rejectWithValue(error);
    }

    return undefined;
  },
  { serializeError },
);
